import { useCallback, useSyncExternalStore } from 'react';

export interface AssetLeaderboardSnapshotProgress {
  loadedAssetCount: number;
  assetCount: number;
}

const EMPTY_PROGRESS: AssetLeaderboardSnapshotProgress = { loadedAssetCount: 0, assetCount: 0 };

/**
 * Per-cache-key fetch progress for the shared Asset Leaderboard snapshot endpoint, kept outside Redux.
 *
 * RTK Query's `updateCachedData` (the usual way to stream incremental updates into a cache entry) is a
 * no-op until the query has resolved at least once — but the loading bar this drives is only ever shown
 * during exactly that window (before first data lands), so cache-based streaming can't carry it. This
 * module-level store fills that gap: every mounted panel subscribing to the same snapshot cache key
 * reads the same counters, so N panels sharing one fetch cycle also share one progress bar, without
 * routing through the store.
 */
const progressByKey = new Map<string, AssetLeaderboardSnapshotProgress>();
const listenersByKey = new Map<string, Set<() => void>>();

function notify(key: string): void {
  listenersByKey.get(key)?.forEach(listener => listener());
}

/** Starts a new fetch cycle for `key`: resets the loaded count and records the batch total. */
export function resetAssetLeaderboardSnapshotProgress(key: string, assetCount: number): void {
  progressByKey.set(key, { loadedAssetCount: 0, assetCount });
  notify(key);
}

/**
 * Advances `key`'s loaded count as a batch settles (success or failure alike). A no-op if `key` has no
 * entry — the last subscriber for it unmounted mid-cycle and cleaned it up (see `subscribeTo...` below)
 * while its batches were still in flight (unsubscribing doesn't abort the underlying fetch). Without this
 * guard, a batch settling after that cleanup would silently re-create the entry from a zeroed baseline —
 * a stray entry nothing will ever read, and if the same universe reopens later, a progress bar that
 * starts from a stale, wrong count until its own `reset` call lands.
 */
export function advanceAssetLeaderboardSnapshotProgress(key: string, settledCount: number): void {
  const current = progressByKey.get(key);
  if (!current) {
    return;
  }
  progressByKey.set(key, { ...current, loadedAssetCount: current.loadedAssetCount + settledCount });
  notify(key);
}

function getAssetLeaderboardSnapshotProgress(key: string): AssetLeaderboardSnapshotProgress {
  return progressByKey.get(key) ?? EMPTY_PROGRESS;
}

function subscribeToAssetLeaderboardSnapshotProgress(key: string, listener: () => void): () => void {
  let listeners = listenersByKey.get(key);
  if (!listeners) {
    listeners = new Set();
    listenersByKey.set(key, listeners);
  }
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      listenersByKey.delete(key);
      // No panel is watching this universe's progress anymore — drop it rather than growing unbounded
      // across every distinct universe signature seen over a long-lived session.
      progressByKey.delete(key);
    }
  };
}

/** Live progress for `key` (a snapshot cache signature), shared across every panel reading the same key. */
export function useAssetLeaderboardSnapshotProgress(key: string): AssetLeaderboardSnapshotProgress {
  const subscribe = useCallback(
    (listener: () => void) => subscribeToAssetLeaderboardSnapshotProgress(key, listener),
    [key]
  );
  const getSnapshot = useCallback(() => getAssetLeaderboardSnapshotProgress(key), [key]);
  return useSyncExternalStore(subscribe, getSnapshot);
}
